import { FileText, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CONTRACT_TYPES } from "@/lib/contracts";
import { CounterpartyField } from "./CounterpartyField";
import { CURRENCIES, fmtAmountDisplay, normName } from "./contractCreateShared";

import { translateFr as tfr } from "@/i18n";
export const ContractForm = ({ form, patch, players, clubs, tf, locale, registerRef, highlightKey, mode }) => {
  const hl = (k) => (highlightKey === k ? "ring-2 ring-red-500/40 border-red-500/50" : "");
  const externalName = form.player_name && !players.some((p) => normName(p.name) === normName(form.player_name)) ? form.player_name : "";
  const athleteValue = form.player_id || (externalName ? "__ext" : "none");

  return (
    <div className="space-y-5 min-w-0" data-testid="contract-info-form">
      <div>
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-foreground/[0.04] dark:bg-white/[0.05] border border-border dark:border-white/[0.08] flex items-center justify-center shrink-0">
            <FileText className="h-4 w-4 text-muted-foreground" />
          </div>
          <h3 className="text-sm font-semibold">{tf("Informations du contrat")}</h3>
        </div>
        {mode === "import" && (
          <p className="text-xs text-muted-foreground mt-1.5">{tf("Ces informations seront pré-remplies et peuvent être modifiées.")}</p>
        )}
      </div>

      <div className="grid gap-2">
        <Label htmlFor="cf-title">{tf("Titre du contrat")} <span className="text-red-500">*</span></Label>
        <Input id="cf-title" className={cn("h-11 sm:h-10", hl("title"))} data-testid="contract-title-input"
          ref={registerRef("title")} value={form.title} onChange={(e) => patch({ title: e.target.value })}
          placeholder={tf("Ex. Mandat de représentation — Kylian Martin")} />
      </div>

      <div className="grid sm:grid-cols-2 gap-5 sm:gap-4 w-full">
        <div className="grid gap-2 min-w-0">
          <Label>{tf("Type de contrat")}</Label>
          <Select value={form.contract_type} onValueChange={(v) => patch({ contract_type: v })}>
            <SelectTrigger className="h-11 sm:h-10 w-full min-w-0" data-testid="contract-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CONTRACT_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{tf(t.label)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid gap-2 min-w-0">
          <Label>{tf("Athlète")}</Label>
          <Select value={athleteValue}
            onValueChange={(v) => {
              if (v === "none") patch({ player_id: "", player_name: "" });
              else if (v === "__ext") patch({ player_id: "" });
              else patch({ player_id: v, player_name: "" });
            }}>
            <SelectTrigger ref={registerRef("player")} className={cn("h-11 sm:h-10 w-full min-w-0", hl("player"))} data-testid="contract-player-select">
              <span className="flex items-center gap-2 min-w-0">
                <User className="h-4 w-4 text-muted-foreground shrink-0" />
                <SelectValue placeholder={tf("Aucun")} />
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{tf("Aucun")}</SelectItem>
              {players.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              {externalName && <SelectItem value="__ext">{externalName} {tf("(externe)")}</SelectItem>}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-2">
        <Label>{tf("Contrepartie")}</Label>
        <CounterpartyField value={form.counterparty} onChange={(v) => patch({ counterparty: v })}
          clubs={clubs} tf={tf} innerRef={registerRef("counterparty")} highlight={highlightKey === "counterparty"} />
      </div>

      <div className="grid min-[360px]:grid-cols-2 gap-5 min-[360px]:gap-4 w-full">
        <div className="grid gap-2 min-w-0">
          <Label htmlFor="cf-start">{tf("Date de début")}</Label>
          <Input id="cf-start" type="date" className={cn("h-11 sm:h-10 w-full min-w-0 max-w-full", hl("start_date"))}
            ref={registerRef("start_date")} data-testid="contract-start-input"
            value={form.start_date} onChange={(e) => patch({ start_date: e.target.value })} />
        </div>
        <div className="grid gap-2 min-w-0">
          <Label htmlFor="cf-end">{tf("Date de fin")}</Label>
          <Input id="cf-end" type="date" className={cn("h-11 sm:h-10 w-full min-w-0 max-w-full", hl("end_date"))}
            ref={registerRef("end_date")} data-testid="contract-end-input"
            value={form.end_date} onChange={(e) => patch({ end_date: e.target.value })} />
        </div>
      </div>

      <div className="grid min-[360px]:grid-cols-2 gap-5 min-[360px]:gap-4 w-full">
        <div className="grid gap-2 min-w-0">
          <Label htmlFor="cf-amount">{tf("Montant annuel")}</Label>
          <div className={cn("flex w-full min-w-0 rounded-md", highlightKey === "amount" && "ring-2 ring-red-500/40 rounded-md")}>
            <Select value={form.currency} onValueChange={(v) => patch({ currency: v })}>
              <SelectTrigger ref={registerRef("currency")} className="h-11 sm:h-10 w-[84px] shrink-0 rounded-r-none border-r-0" data-testid="contract-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{tfr(c)}</SelectItem>)}</SelectContent>
            </Select>
            <Input id="cf-amount" inputMode="decimal" className="h-11 sm:h-10 flex-1 min-w-0 rounded-l-none" data-testid="contract-amount-input"
              ref={registerRef("amount")} value={form.amount}
              onChange={(e) => patch({ amount: e.target.value })}
              onBlur={() => { const d = fmtAmountDisplay(form.amount, locale); if (d) patch({ amount: d }); }}
              placeholder={locale === "fr-FR" ? "240 000" : "240,000"} />
          </div>
        </div>
        <div className="grid gap-2 min-w-0">
          <Label htmlFor="cf-commission">{tf("Commission (%)")}</Label>
          <Input id="cf-commission" inputMode="decimal" className={cn("h-11 sm:h-10 w-full min-w-0 max-w-full", hl("commission"))}
            ref={registerRef("commission")} data-testid="contract-commission-input"
            value={form.commission_percent} onChange={(e) => patch({ commission_percent: e.target.value })} placeholder="Ex. 8" />
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="cf-notes">{tf("Notes")}</Label>
        <Textarea id="cf-notes" rows={3} data-testid="contract-description-input" value={form.description}
          onChange={(e) => patch({ description: e.target.value })}
          placeholder={tf("Contexte, clauses clés, points de vigilance…")} />
      </div>
    </div>
  );
};
